import React from 'react';
import {
  BrainCircuit,
  ShieldAlert,
  Code2,
  Sparkles,
  Video,
  Globe,
  FileEdit,
  Heart,
  AlertTriangle,
  CheckCircle2,
  MessageCircleMore,
  PhoneCall,
  Mic2,
  Mic,
} from 'lucide-react';

/**
 * Features — "End-to-end recruitment" shown as a MODERN ASYMMETRIC
 * BENTO GRID (the 2026 Linear/Vercel treatment) instead of uniform
 * card rows.
 *
 * Grid anatomy (lg, 6 columns):
 *   ┌─────────────────────────┬────────────┐
 *   │                         │ Resume     │
 *   │  FLAGSHIP (dark, 4×2)   ├────────────┤
 *   │  Realtime AI Interview  │ Cheating   │
 *   ├────────┬────────┬───────┴────────────┤
 *   │ Coding │ JD AI  │ Career Pages       │
 *   ├────────┼────────┼────────────────────┤
 *   │ Live   │ Person.│ Phone + WhatsApp   │
 *   └────────┴────────┴────────────────────┘
 *
 * Design rules:
 *   • The flagship tile uses the brand's dark green ink
 *     (hsl(164 33% 7%)) — same surface as the Security band and
 *     FinalCTA, so the dark accents feel like one family.
 *   • Visuals "edge-bleed": they crop at the tile's bottom edge
 *     (overflow-hidden + negative bottom margin) for the modern
 *     screenshot-peeking-out-of-the-card look.
 *   • Copy is terse — one line per tile. Outcome words, no tech
 *     implementation details. All claims match the FAQ.
 *   • Hover: primary glow + lift (consistent with the page system).
 */
export default function HiringFeatures() {
  return (
    <section
      className="relative py-14 lg:py-20 overflow-hidden scroll-mt-16"
      id="features"
    >
      {/* ─── Background stack ───
          The cards are the same cream as the page background, so the
          section needs its own surface for the bento to pop:
          1. sage-tinted band (fades back to cream at both ends)
          2. dotted grid, radially masked toward the center
          3. two soft brand-green glow blobs                         */}
      <div className="absolute inset-0 -z-30 bg-gradient-to-b from-background via-[hsl(164_26%_92%)] to-background pointer-events-none" />

      <div
        className="absolute inset-0 -z-20 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(hsl(var(--primary) / 0.45) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage:
            'radial-gradient(ellipse 75% 65% at 50% 38%, black 25%, transparent 78%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 75% 65% at 50% 38%, black 25%, transparent 78%)',
        }}
      />

      <div className="absolute top-[5%] right-[-12%] w-[48%] h-[48%] bg-emerald-500/[0.13] rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute bottom-[-8%] left-[-12%] w-[42%] h-[42%] bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />
      <div className="absolute top-[40%] left-[30%] w-[35%] h-[35%] bg-teal-500/[0.07] rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ─── Section heading ─── */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            End-to-end recruitment
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            Your recruiters, multiplied.
            <br />
            <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              AI does the heavy lifting.
            </span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Every stage of hiring job creation, sourcing, screening, assessing and onboarding handled by AI agents on one platform, so your
            team ships 10× faster.
          </p>
        </div>

        {/* ═══════════════ BENTO GRID ═══════════════ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-5">
          {/* ─── FLAGSHIP — Realtime AI Video Interview (dark, 4×2) ─── */}
          <FlagshipTile />

          {/* ─── Resume Screening (2 col) ─── */}
          <BentoTile
            icon={BrainCircuit}
            title="AI Resume Screening"
            desc="Upload one resume or a thousand — every candidate scored and ranked against your JD."
            className="lg:col-span-2"
          >
            <ResumeScoreVisual />
          </BentoTile>

          {/* ─── Cheating Detection (2 col) ─── */}
          <BentoTile
            icon={ShieldAlert}
            title="Cheating Detection"
            desc="Seven signals catch copied code, AI submissions and impersonation — with a plain-English verdict."
            className="lg:col-span-2"
          >
            <CheatSignalsVisual />
          </BentoTile>

          {/* ─── Coding Tests (2 col) ─── */}
          <BentoTile
            icon={Code2}
            title="Coding Tests + AI Follow-Up"
            desc="Secure tests in 10 languages. After submission, AI interviews candidates about their own code."
            className="lg:col-span-2"
            bleed
          >
            <CodeEditorVisual />
          </BentoTile>

          {/* ─── JD Creator (2 col) ─── */}
          <BentoTile
            icon={FileEdit}
            title="AI Job Description Creator"
            desc="Speak the role or type keywords — an ATS-ready JD with screening questions in under a minute."
            className="lg:col-span-2"
          >
            <JDVoiceVisual />
          </BentoTile>

          {/* ─── Career Pages (2 col) ─── */}
          <BentoTile
            icon={Globe}
            title="Branded Career Pages"
            desc="A careers portal at yourcompany.searchjob.ai — showcase culture, list roles, accept applications."
            className="lg:col-span-2"
            bleed
          >
            <CareerPageVisual />
          </BentoTile>

          {/* ─── Live Video Interviews (2 col) ─── */}
          <BentoTile
            icon={Video}
            title="Live Video Interviews"
            desc="Panel rounds with auto-recording, live transcripts and AI-generated summaries."
            className="lg:col-span-2"
          >
            <PanelTilesVisual />
          </BentoTile>

          {/* ─── Personality & Behavioural (2 col) ─── */}
          <BentoTile
            icon={Heart}
            title="Personality & Behavioural"
            desc="Big Five-scored assessments for culture fit, work style and communication."
            className="lg:col-span-2"
          >
            <TraitsVisual />
          </BentoTile>

          {/* ─── Phone & WhatsApp screening — live (2 col) ─── */}
          <ChannelScreeningTile />
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  TILE SHELLS                                                     */
/* ═══════════════════════════════════════════════════════════════ */

/**
 * BentoTile — generic light tile. Header (icon chip + title + one-
 * liner) on top, visual fills the rest. `bleed` lets the visual crop
 * at the bottom edge of the card.
 */
function BentoTile({ icon: Icon, title, desc, className = '', bleed = false, children }) {
  return (
    <div
      className={`group relative bg-card border border-border/70 rounded-3xl overflow-hidden hover:border-primary/40 hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.28)] hover:-translate-y-1 transition-all duration-300 flex flex-col ${className}`}
    >
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/10 opacity-0 group-hover:opacity-100 blur-3xl rounded-full transition-opacity duration-500 pointer-events-none" />

      <div className="relative p-6 pb-4">
        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25 mb-4">
          <Icon className="w-5 h-5 text-primary-foreground" />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-1.5">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
      </div>

      <div
        className={`relative mt-auto ${
          bleed ? 'px-6 -mb-3' : 'px-6 pb-6'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * FlagshipTile — the hero of the bento: Realtime AI Video Interview
 * on the brand's dark ink surface, spanning 4 columns × 2 rows, with
 * a live-call mockup that bleeds off the bottom edge.
 */
function FlagshipTile() {
  return (
    <div
      className="group relative lg:col-span-4 lg:row-span-2 md:col-span-2 rounded-3xl overflow-hidden border border-white/10 hover:border-emerald-400/40 hover:shadow-[0_20px_70px_-20px_rgba(16,185,129,0.45)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
      style={{ backgroundColor: 'hsl(164 33% 7%)' }}
    >
      {/* Glow + grid texture */}
      <div className="absolute top-[-30%] right-[-15%] w-[70%] h-[70%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      <div className="relative p-6 lg:p-8 pb-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
            <Video className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            Flagship
          </span>
        </div>

        <h3 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-2">
          Realtime AI Video Interview
        </h3>
        <p className="text-sm lg:text-base text-white/60 leading-relaxed max-w-lg mb-4">
          The AI conducts the entire screening interview as a live video
          call — 15+ questions personalised from each candidate&apos;s
          resume and your JD, available 24/7. Your team only meets the
          candidates worth hiring.
        </p>

        {/* Language chips */}
        <div className="flex flex-wrap gap-1.5">
          {['multilingual'].map((lang) => (
            <span
              key={lang}
              className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-white/80"
            >
              {lang}
            </span>
          ))}
        </div>
      </div>

      {/* ─── Live call mockup — bleeds off the bottom edge ─── */}
      <div className="relative mt-auto px-6 lg:px-8 -mb-2">
        <div className="rounded-t-2xl border border-b-0 border-white/15 bg-zinc-950/80 backdrop-blur-sm overflow-hidden shadow-2xl">
          {/* Call top bar */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-[10px] font-semibold text-white/80">
              Round 1 · Senior Frontend Engineer
            </span>
            <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-semibold">
              <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
              REC · 12:34
            </span>
          </div>

          {/* Two video tiles */}
          <div className="grid grid-cols-2 gap-1.5 p-2">
            {/* AI interviewer — AI avatar, no real photo */}
            <div className="relative aspect-[16/8] rounded-lg bg-gradient-to-br from-emerald-500/25 via-emerald-600/15 to-teal-600/20 border border-emerald-400/20 flex items-center justify-center overflow-hidden">
              <div className="w-10 h-10 rounded-full bg-emerald-400 flex items-center justify-center shadow-xl">
                <Sparkles className="w-5 h-5 text-emerald-950" />
              </div>
              <span className="absolute bottom-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider text-emerald-100 bg-emerald-950/70 px-1.5 py-0.5 rounded">
                AI Interviewer
              </span>
              <span className="absolute top-1.5 right-1.5 flex items-end gap-0.5">
                {[3, 6, 4, 7, 5].map((h, i) => (
                  <span
                    key={i}
                    className="w-0.5 bg-emerald-300/90 rounded-full animate-pulse"
                    style={{ height: `${h}px`, animationDelay: `${i * 0.12}s` }}
                  />
                ))}
              </span>
            </div>

            {/* Candidate — real candidate photo (public/interviewer.png) */}
            <div className="relative aspect-[16/8] rounded-lg bg-zinc-800 border border-white/10 overflow-hidden">
              <img
                src="/interviewer.png"
                alt="Candidate"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider text-white/90 bg-black/50 px-1.5 py-0.5 rounded">
                <Mic className="w-2 h-2" />
                Priya S.
              </span>
            </div>
          </div>

          {/* Live transcript line — crops at the card edge */}
          <div className="px-3 pb-3 pt-0.5">
            <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold mb-0.5">
              Live transcript
            </div>
            <div className="text-[10px] text-white/85 italic truncate">
              &ldquo;Walk me through the most challenging bug you fixed in
              production…&rdquo;
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ChannelScreeningTile — the two live multi-channel screening agents:
 * an AI phone call (HR voice screen) and a WhatsApp chat agent that
 * screens and updates candidates. Both ship today, so this is a full
 * "Live" tile (not a coming-soon placeholder).
 */
function ChannelScreeningTile() {
  return (
    <div className="group relative lg:col-span-2 bg-card border border-border/70 rounded-3xl overflow-hidden hover:border-primary/40 hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.28)] hover:-translate-y-1 transition-all duration-300 flex flex-col">
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/10 opacity-0 group-hover:opacity-100 blur-3xl rounded-full transition-opacity duration-500 pointer-events-none" />

      <div className="relative p-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex -space-x-2">
            <span className="w-10 h-10 rounded-2xl bg-indigo-500 border-2 border-card flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <PhoneCall className="w-5 h-5 text-white" />
            </span>
            <span className="w-10 h-10 rounded-2xl bg-green-500 border-2 border-card flex items-center justify-center shadow-lg shadow-green-500/25">
              <MessageCircleMore className="w-5 h-5 text-white" />
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Live
          </span>
        </div>

        <h3 className="text-lg font-bold text-foreground mb-1.5">
          Phone &amp; WhatsApp Screening
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          An AI agent calls candidates for a quick HR voice screen —
          confirming interest, CTC and notice period — while a WhatsApp
          agent screens and updates them on the channel India actually
          answers.
        </p>
      </div>

      {/* Two-channel mini visual */}
      <div className="relative mt-auto px-6 pb-6">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-border bg-muted/40 p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <PhoneCall className="w-3 h-3 text-indigo-600" />
              <span className="text-[10px] font-semibold text-foreground">
                Voice screen
              </span>
            </div>
            <div className="text-[9px] text-muted-foreground leading-snug">
              Interest ✓ · CTC ✓ · Notice ✓
            </div>
          </div>
          <div className="rounded-xl border border-border bg-muted/40 p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MessageCircleMore className="w-3 h-3 text-green-600" />
              <span className="text-[10px] font-semibold text-foreground">
                WhatsApp
              </span>
            </div>
            <div className="text-[9px] text-muted-foreground leading-snug">
              Screened · &ldquo;You&apos;re shortlisted ✓&rdquo;
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  INLINE VISUALS — mini product mockups                           */
/* ═══════════════════════════════════════════════════════════════ */

function ResumeScoreVisual() {
  const skills = [
    { name: 'Skill match', score: 95 },
    { name: 'Experience', score: 88 },
    { name: 'Overall fit', score: 87 },
  ];
  return (
    <div className="bg-muted/40 border border-border/50 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Resume Score
        </span>
        <span className="text-lg font-bold text-foreground">87</span>
      </div>
      {skills.map((s) => (
        <div key={s.name}>
          <div className="flex justify-between items-center mb-0.5">
            <span className="text-[11px] text-foreground/80">{s.name}</span>
            <span className="text-[10px] font-semibold text-foreground/60">
              {s.score}
            </span>
          </div>
          <div className="h-1.5 bg-background rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${s.score}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function CheatSignalsVisual() {
  return (
    <div className="bg-muted/40 border border-border/50 rounded-xl p-3 space-y-1.5">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="w-4 h-4 text-red-600" />
        <span className="text-[11px] font-semibold text-red-700">
          Verdict — High Risk
        </span>
        <span className="ml-auto text-base font-bold text-red-700">72%</span>
      </div>
      <Signal label="Code copied from another candidate" tone="red" />
      <Signal label="AI-written code detected" tone="red" />
      <Signal label="Identity verified" tone="green" />
      <div className="pt-1.5 mt-0.5 border-t border-border/50 text-[9px] text-muted-foreground text-center">
        22 proctoring snapshots · evidence report ready
      </div>
    </div>
  );
}

function Signal({ label, tone }) {
  const toneClass = tone === 'red' ? 'text-red-700' : 'text-green-700';
  const Icon = tone === 'red' ? AlertTriangle : CheckCircle2;
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <Icon className={`w-3 h-3 ${toneClass}`} />
      <span className="text-foreground/70 flex-1 truncate">{label}</span>
    </div>
  );
}

function CodeEditorVisual() {
  return (
    <div className="bg-zinc-950 border border-b-0 border-zinc-800 rounded-t-xl p-3 font-mono text-[10px]">
      <div className="flex items-center gap-1 mb-2">
        <div className="w-2 h-2 rounded-full bg-red-400/70" />
        <div className="w-2 h-2 rounded-full bg-yellow-400/70" />
        <div className="w-2 h-2 rounded-full bg-green-400/70" />
        <span className="ml-2 text-zinc-500 text-[9px]">solution.py</span>
        <span className="ml-auto text-[9px] text-green-400">● Recording</span>
      </div>
      <div className="space-y-0.5 text-zinc-300">
        <div>
          <span className="text-purple-400">def</span>{' '}
          <span className="text-blue-400">two_sum</span>(
          <span className="text-orange-400">nums, target</span>):
        </div>
        <div className="pl-3 text-zinc-500"># hash map · O(n)</div>
        <div className="pl-3">
          <span className="text-purple-400">return</span>{' '}
          [<span className="text-emerald-400">i</span>,{' '}
          <span className="text-emerald-400">j</span>]
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center justify-between">
        <span className="text-[9px] text-zinc-500">10 languages</span>
        <span className="text-[9px] text-emerald-400 font-semibold">
          AI follow-up pending
        </span>
      </div>
    </div>
  );
}

function JDVoiceVisual() {
  return (
    <div className="bg-muted/40 border border-border/50 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
        <Mic2 className="w-4 h-4 text-primary" />
        <span className="text-[11px] font-semibold text-foreground truncate">
          &ldquo;Hire a senior React developer…&rdquo;
        </span>
        <span className="ml-auto text-[9px] text-primary font-semibold animate-pulse whitespace-nowrap">
          AI writing
        </span>
      </div>
      <div className="space-y-1">
        <div className="text-[10px] font-bold text-foreground">
          Senior Frontend Engineer
        </div>
        {[
          '4+ years React production experience',
          'TypeScript, Redux, modern testing',
          '5 screening questions pre-built',
        ].map((line) => (
          <div
            key={line}
            className="text-[9px] text-muted-foreground flex items-center gap-1"
          >
            <CheckCircle2 className="w-2.5 h-2.5 text-primary shrink-0" />
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

function CareerPageVisual() {
  return (
    <div className="bg-background border border-b-0 border-border rounded-t-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-muted/40 border-b border-border">
        <span className="w-1.5 h-1.5 rounded-full bg-red-400/70" />
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400/70" />
        <span className="w-1.5 h-1.5 rounded-full bg-green-400/70" />
        <span className="ml-1.5 text-[9px] text-muted-foreground font-mono truncate">
          acme.searchjob.ai/careers
        </span>
      </div>
      <div className="p-2.5 space-y-1.5">
        <div className="text-[10px] font-bold text-foreground">
          Acme Corp · We&apos;re hiring
        </div>
        <div className="h-2 bg-primary/15 rounded" />
        <div className="h-2 bg-muted rounded w-3/4" />
        <div className="flex items-center justify-between pt-0.5">
          <span className="text-[8px] text-muted-foreground">18 open roles</span>
          <span className="text-[8px] text-primary font-bold">Apply now →</span>
        </div>
      </div>
    </div>
  );
}

function PanelTilesVisual() {
  return (
    <div className="flex gap-1.5">
      <div className="relative flex-1 aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full bg-zinc-600 flex items-center justify-center text-white text-xs font-bold">
          A
        </div>
        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5">
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
          <span className="text-[8px] text-white font-semibold">REC</span>
        </span>
        <span className="absolute bottom-1.5 left-1.5 text-[8px] text-white/80 bg-black/40 px-1 rounded">
          Live transcript on
        </span>
      </div>
      <div className="w-14 flex flex-col gap-1.5">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="aspect-video rounded bg-zinc-700/60 flex items-center justify-center"
          >
            <div className="w-3 h-3 rounded-full bg-zinc-500" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TraitsVisual() {
  return (
    <div className="bg-muted/40 border border-border/50 rounded-xl p-3 space-y-1.5">
      <TraitBar label="Openness" value={88} />
      <TraitBar label="Conscientiousness" value={92} />
      <TraitBar label="Extraversion" value={68} />
      <TraitBar label="Agreeableness" value={84} />
    </div>
  );
}

function TraitBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-[9px] mb-0.5">
        <span className="text-muted-foreground truncate">{label}</span>
        <span className="font-bold text-foreground">{value}</span>
      </div>
      <div className="h-1 bg-background rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}
